import { Field, useFormikContext } from "formik";
import React, { Fragment, forwardRef, useState } from "react";
import Select, { components } from "react-select";
import DownArrowIcon from "../../assets/icons/down-arrow-icon";
import StarIcon from "../../assets/icons/req-star";
import ErrorMsg from "./error-msg";

const HybridSelectField = forwardRef(
  (
    {
      key,
      label,
      isDisabled = false,
      style = {},
      defaultValue = null,
      Icon = <DownArrowIcon />,
      name = "",
      options = [],
      containerClass = "mb-2",
      placeholder,
      id,
      isContainer = true,
      labelClass = "whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]",
      handleSelected,
      defaultInputClass = "min-h-[42px] rounded-[8px] bg-[var(--input-bg)] text-[var(--input-color)] text-[length:var(--input-font-size)] font-[var(--input-font-weight)]",
      errorBorder = "border border-[var(--input-error-border)]",
      inputBorder = "border border-[var(--input-border)]",
      selectClass = "",
      replaceSelectCSS = false,
      errMsgContainerClass = "",
      isClearable = true,
      isSearchable = true,
      children,
      customStyle,
      labelWidth = "10rem",
      isRequired = false,
      menuPosition = "fixed",
      autoFocus = false,
      general_color = true,
      menuWidth = "",
      parentClass = "w-full flex flex-col gap-2",
      onFocus = () => {},
      // New props for hybrid functionality
      allowCustomInput = true,
      customInputPlaceholder = "Type your custom value...",
      suffix = "@abdm", // Default suffix like @abdm
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const {
      values: formValues,
      setFieldValue,
      setFieldTouched,
    } = useFormikContext();

    const Container = isContainer ? "div" : Fragment;

    // Custom DropdownIndicator to show the arrow
    const DropdownIndicator = (props) => {
      return (
        <components.DropdownIndicator {...props}>
          <div className="p-2">{Icon}</div>
        </components.DropdownIndicator>
      );
    };

    // Get current value for select
    const selectValue = (form) => {
      if (form.values[name]) {
        const foundOption = options?.find(
          (item) => item.value === form.values[name]
        );
        return (
          foundOption || {
            label: form.values[name],
            value: form.values[name],
          }
        );
      }
      return defaultValue ?? null;
    };

    // Custom styles to match the image design
    const customStyles = {
      indicatorSeparator: () => ({ display: "none" }),
      control: (base, state) => ({
        ...base,
        border: "none",
        borderRadius: "8px",
        backgroundColor: "var(--input-bg)",
        boxShadow: "none",
        minHeight: "42px",
        fontFamily: "Inter, sans-serif",
        fontSize: "14px",
        "&:hover": {
          borderColor: "transparent",
        },
      }),
      valueContainer: (base) => ({
        ...base,
        padding: "0 12px",
      }),
      input: (base) => ({
        ...base,
        margin: "0",
        paddingTop: "0",
        paddingBottom: "0",
      }),
      placeholder: (base) => ({
        ...base,
        color: "var(--input-place-color)",
        fontFamily: "Inter, sans-serif",
        fontSize: "14px",
        fontWeight: "400",
      }),
      singleValue: (base) => ({
        ...base,
        color: "var(--input-color)",
        fontFamily: "Inter, sans-serif",
        fontSize: "14px",
        fontWeight: "400",
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "#e5ecffb2"
          : state.isFocused
          ? "#f5f5f5"
          : "white",
        color: "var(--input-color)",
        fontFamily: "Inter, sans-serif",
        fontSize: "14px",
        padding: "10px 12px",
        "&:hover": {
          backgroundColor: "#f5f5f5",
        },
      }),
      menu: (base) => ({
        ...base,
        zIndex: 9999,
        border: "1px solid var(--input-border)",
        borderRadius: "8px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      dropdownIndicator: (base, state) => ({
        ...base,
        color: "var(--input-color)",
        padding: "0 8px",
        transform: state.selectProps.menuIsOpen
          ? "rotate(180deg)"
          : "rotate(0deg)",
        transition: "transform 0.2s",
      }),
    };

    return (
      <Container
        {...(isContainer && containerClass
          ? { className: containerClass }
          : {})}
      >
        <Field name={name}>
          {({ form, meta }) => {
            return (
              <div className={parentClass}>
                {label && (
                  <label
                    style={{ minWidth: labelWidth }}
                    {...(id ? { htmlFor: id } : {})}
                    className={`flex items-center ${labelClass}`}
                  >
                    {label}
                    {isRequired && (
                      <span className="ml-2 flex items-center">
                        <StarIcon />
                      </span>
                    )}
                  </label>
                )}

                {children}

                <div
                  className={`w-full relative ${
                    meta.error && meta.touched ? errorBorder : inputBorder
                  } rounded-lg overflow-hidden`}
                >
                  <div className="flex items-center w-full">
                    <div className="flex-1">
                      <Select
                        ref={ref}
                        menuPosition={menuPosition}
                        menuPortalTarget={document.body}
                        {...(key ? { key: key } : {})}
                        isSearchable={isSearchable}
                        isClearable={isClearable}
                        isDisabled={isDisabled}
                        defaultValue={defaultValue}
                        value={selectValue(form)}
                        name={name}
                        {...(id ? { id: id } : {})}
                        components={{
                          DropdownIndicator,
                        }}
                        styles={customStyles}
                        className="w-full"
                        onFocus={() => {
                          setIsFocused(true);
                          onFocus();
                        }}
                        placeholder={placeholder}
                        options={options}
                        onChange={(selectedOption) => {
                          if (selectedOption) {
                            form.setFieldValue(name, selectedOption.value);
                            handleSelected && handleSelected(selectedOption);
                          } else {
                            // If cleared, reset the field
                            form.setFieldValue(name, "");
                          }
                        }}
                        // Handle input changes when user types
                        onInputChange={(inputValue, { action }) => {
                          if (action === "input-change" && allowCustomInput) {
                            // Store the typed text temporarily
                            // const finalValue = suffix ? `${inputValue}${suffix}` : inputValue;
                            form.setFieldValue(name, inputValue);
                          }
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && allowCustomInput) {
                            const currentInput = event.target.value;
                            if (currentInput) {
                              // Always use the typed text, regardless of dropdown options
                              //   const finalValue = suffix ? `${currentInput}${suffix}` : currentInput;
                              form.setFieldValue(name, currentInput);
                              event.preventDefault();
                            }
                          }
                        }}
                        onBlur={(event) => {
                          setIsFocused(false);
                          form.setFieldTouched(name, true);

                          // On blur, if there's typed text but no selection, use the typed text
                          const inputValue = event.target.value;
                          if (inputValue && allowCustomInput) {
                            const finalValue = suffix
                              ? `${inputValue}${suffix}`
                              : inputValue;
                            form.setFieldValue(name, inputValue);
                          }
                        }}
                        // Enable typing and filtering
                        filterOption={(option, inputValue) => {
                          return option.label
                            .toLowerCase()
                            .includes(inputValue.toLowerCase());
                        }}
                      />
                    </div>

                    {suffix && (
                      <div className="absolute right-8 top-1/2 transform -translate-y-1/2 text-[#9CA3AF] pointer-events-none text-sm font-normal">
                        {/* {suffix} */}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          }}
        </Field>
        <ErrorMsg
          containerClass={errMsgContainerClass}
          name={name}
          customStyle={customStyle}
        />
      </Container>
    );
  }
);

export default HybridSelectField;
